import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNavLayout } from '@/hooks/useNavLayout';
import { useBreadcrumbVisible } from '@/hooks/useBreadcrumbVisible';
import { SidebarNav, TopbarNav, BottomBarNav } from '@/components/dashboard/DashboardNav';
import { DashboardBreadcrumb } from '@/components/dashboard/DashboardBreadcrumb';
import { cn } from '@/lib/utils';
import { FOCUS_MODE_TOGGLE_EVENT } from '@/lib/editorEvents';

const FOCUS_MODE_STORAGE_KEY = 'formatafacil-focus-mode';

function read_focus_mode(): boolean {
  try { return localStorage.getItem(FOCUS_MODE_STORAGE_KEY) === '1'; } catch { return false; }
}

export function DashboardLayout({ children }: { children: ReactNode }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const handle_sign_out = useCallback(async () => {
    await signOut();
    navigate('/auth');
  }, [signOut, navigate]);
  const { layout } = useNavLayout();
  const { breadcrumb_visible } = useBreadcrumbVisible();
  const [focus_mode, set_focus_mode] = useState(read_focus_mode);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ enabled: boolean }>).detail;
      set_focus_mode(detail.enabled);
    };
    window.addEventListener(FOCUS_MODE_TOGGLE_EVENT, handler);
    return () => window.removeEventListener(FOCUS_MODE_TOGGLE_EVENT, handler);
  }, []);

  const floating_breadcrumb = breadcrumb_visible ? (
    <div className="sticky top-0 z-30 px-4 pt-3 pb-1">
      <header className="mx-auto max-w-5xl rounded-3xl bg-[hsl(var(--card)/0.95)] backdrop-blur-xl shadow-md px-4 py-2.5 flex items-center">
        <DashboardBreadcrumb />
      </header>
    </div>
  ) : null;

  /** Fundo global herdado do tema */
  const bg_class = 'bg-background';

  if (focus_mode) {
    return (
      <div className={cn('min-h-screen', bg_class)}>
        {floating_breadcrumb}
        <main>{children}</main>
      </div>
    );
  }

  /* Sidebar */
  if (layout === 'sidebar') {
    return (
      <div className={cn('min-h-screen flex', bg_class)}>
        <SidebarNav on_sign_out={handle_sign_out} />
        <div className="flex-1 min-w-0 flex flex-col">
          {floating_breadcrumb}
          <main className="flex-1 min-w-0">{children}</main>
        </div>
      </div>
    );
  }

  /* Topbar */
  if (layout === 'topbar') {
    return (
      <div className={cn('min-h-screen', bg_class)}>
        <TopbarNav on_sign_out={handle_sign_out} />
        {floating_breadcrumb}
        <main>{children}</main>
      </div>
    );
  }

  /* Dock e Bottom Bar */
  return (
    <div className={cn('min-h-screen', bg_class)}>
      {floating_breadcrumb}
      <main className={cn(
        layout === 'bottom-bar' && 'pb-24',
        layout === 'dock' && 'pb-24',
      )}>
        {children}
      </main>
      {layout === 'bottom-bar' && <BottomBarNav />}
    </div>
  );
}
